import { useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  Flame, 
  Snowflake,
  ThermometerSun
} from 'lucide-react';

const VelocityIndicator = ({ velocity, ticketsRemaining, totalTickets }) => {
  // Velocity: HOT (selling fast), WARM (moderate), COLD (slow), or calculate from remaining %
  const percentRemaining = totalTickets > 0 ? (ticketsRemaining / totalTickets) * 100 : null;
  
  let status = 'MODERATE';
  let icon = <ThermometerSun className="w-4 h-4" />;
  let color = 'text-warning';
  let bgColor = 'bg-warning/10';
  
  if (velocity === 'HOT' || (percentRemaining !== null && percentRemaining < 30)) {
    status = 'HOT';
    icon = <Flame className="w-4 h-4" />;
    color = 'text-danger';
    bgColor = 'bg-danger/10';
  } else if (velocity === 'COLD' || (percentRemaining !== null && percentRemaining > 70)) {
    status = 'COLD';
    icon = <Snowflake className="w-4 h-4" />;
    color = 'text-accent';
    bgColor = 'bg-accent/10';
  }
  
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded ${bgColor} ${color}`}>
      {icon}
      <span className="text-xs font-medium">{status}</span>
    </div>
  );
};

const TrendArrow = ({ trend, value }) => {
  if (trend === 'IMPROVING' || value > 5) {
    return (
      <div className="flex items-center gap-1 text-success">
        <TrendingUp className="w-4 h-4" />
        <span className="text-xs">+{Math.abs(value || 5).toFixed(1)}%</span>
      </div>
    );
  }
  if (trend === 'DECLINING' || value < -5) {
    return (
      <div className="flex items-center gap-1 text-danger">
        <TrendingDown className="w-4 h-4" />
        <span className="text-xs">{value ? value.toFixed(1) : '-5.0'}%</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 text-muted-foreground">
      <Minus className="w-4 h-4" />
      <span className="text-xs">Stable</span>
    </div>
  );
};

export const VelocityTracker = ({ games, title = "Sales Velocity Tracker" }) => {
  const velocityData = useMemo(() => {
    return games
      .filter(g => g.remaining_tickets || g.estimated_tickets_remaining)
      .slice(0, 10)
      .map(game => {
        const remaining = game.remaining_tickets || game.estimated_tickets_remaining || 0;
        const total = game.total_tickets || remaining * 2; // Estimate if not available
        const percentRemaining = total > 0 ? (remaining / total) * 100 : 50;
        
        // Determine velocity based on prizes remaining percentage
        let velocity = 'MODERATE';
        if (game.prizes_remaining < 30) velocity = 'HOT';
        else if (game.prizes_remaining > 70) velocity = 'COLD';
        
        return {
          ...game,
          percentRemaining,
          velocity,
          remaining,
          total
        };
      })
      .sort((a, b) => a.percentRemaining - b.percentRemaining); // Sort by most depleted first
  }, [games]);

  if (velocityData.length === 0) {
    return null;
  }

  return (
    <Card data-testid="velocity-tracker">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <Flame className="w-4 h-4 text-danger" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {velocityData.slice(0, 6).map((game, idx) => (
          <div key={game.game_id || idx} className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-sm font-medium truncate">{game.name}</span>
                <Badge variant="outline" className="text-xs shrink-0">${game.price}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <VelocityIndicator 
                  velocity={game.velocity}
                  ticketsRemaining={game.remaining}
                  totalTickets={game.total}
                />
                <TrendArrow trend={game.odds_trend} value={game.odds_improvement_percent} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Progress 
                value={game.percentRemaining} 
                className="h-2 flex-1"
              />
              <span className="text-xs text-muted-foreground w-12 text-right">
                {game.percentRemaining.toFixed(0)}%
              </span>
            </div>
          </div>
        ))}
        <div className="pt-2 border-t border-border">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Flame className="w-3 h-3 text-danger" /> HOT = Selling Fast
            </span>
            <span className="flex items-center gap-1">
              <Snowflake className="w-3 h-3 text-accent" /> COLD = Slow Sales
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
